import type { GisSite } from '../api/types'

/** One officer's accountability roll-up over a `GisSite[]` (mandal-scoped OR district-wide — D4). */
export interface MandalOfficerRow {
  /** Stable officer id, or `null` for the unassigned bucket. */
  officerId: string | null
  name: string
  assigned: number
  surveyed: number
  inProgress: number
  overdue: number
  never: number
  /** surveyed / assigned, within this mandal only. */
  completionPct: number
  /** Fewer than 3 sites — too few to over-trust; render a "thin data" tag. */
  thin: boolean
}

const THIN = 3
const UNASSIGNED_KEY = '\u0000unassigned'

const blankRow = (officerId: string | null, name: string): MandalOfficerRow => ({
  officerId,
  name,
  assigned: 0,
  surveyed: 0,
  inProgress: 0,
  overdue: 0,
  never: 0,
  completionPct: 0,
  thin: false,
})

/** Tally a site's survey status into its officer row. */
function addStatus(row: MandalOfficerRow, status: GisSite['status']): void {
  row.assigned += 1
  if (status === 'surveyed') {
    row.surveyed += 1
  } else if (status === 'in_progress') {
    row.inProgress += 1
  } else if (status === 'overdue') {
    row.overdue += 1
  } else {
    row.never += 1
  }
}

/** The non-surveyed "drag" behind an officer's completion — the counts that need pushing. */
export function dragText(r: MandalOfficerRow): string {
  const bits: string[] = []
  if (r.never > 0) {
    bits.push(`${r.never} never`)
  }
  if (r.overdue > 0) {
    bits.push(`${r.overdue} overdue`)
  }
  if (r.inProgress > 0) {
    bits.push(`${r.inProgress} in progress`)
  }
  return bits.length > 0 ? bits.join(' · ') : 'all surveyed'
}

/**
 * Roll a `GisSite[]` up to the officers serving it, **worst-completion-first** — the Coverage
 * "By officer" view. Reused mandal-scoped AND **district-wide** (D4): a pure group-by-`officerId`,
 * scope-agnostic. Completion is `surveyed / assigned` over the passed set; ties break on the biggest
 * drag (never + overdue). Sites with no officer roll up under "Unassigned". Uses `site.officerId`.
 */
export function mandalOfficerRollup(sites: GisSite[]): MandalOfficerRow[] {
  const byOfficer = new Map<string, MandalOfficerRow>()
  for (const s of sites) {
    const key = s.officerId ?? UNASSIGNED_KEY
    const row = byOfficer.get(key) ?? blankRow(s.officerId, s.officerName ?? 'Unassigned')
    addStatus(row, s.status)
    byOfficer.set(key, row)
  }
  const rows = [...byOfficer.values()]
  for (const r of rows) {
    r.completionPct = r.assigned > 0 ? Math.round((r.surveyed / r.assigned) * 100) : 0
    r.thin = r.assigned < THIN
  }
  return rows.sort((a, b) => a.completionPct - b.completionPct || b.never + b.overdue - (a.never + a.overdue))
}
